<template>
  <div>
    <v-row dense>
      <v-col cols="12">
        <v-menu
          ref="menuDateFrom"
          v-model="menuDateFrom"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="form.inhabitant.date_from | dateFormat"
              :label="`${$t('INHABITANTS.DATE_FROM')} *`"
              append-icon="mdi-calendar"
              clearable
              autocomplete="nope"
              role="button"
              readonly
              disabled
              :error-messages="errors.inhabitant && errors.inhabitant.date_from"
              :rules="getRulesByName('date_from')"
              @click:clear="clearFrom"
              v-on="on"
            />
          </template>
          <v-date-picker
            ref="picker"
            v-model="form.inhabitant.date_from"
            min="1940-01-01"
            first-day-of-week="1"
            @change="saveDatefrom"
          />
        </v-menu>
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-if="readonly"
          :value="form.inhabitant.date_to | dateFormat"
          :label="`${$t('INHABITANTS.DATE_TO')} *`"
          :error-messages="errors.inhabitant && errors.inhabitant.date_to"
          append-icon="mdi-calendar"
          autocomplete="nope"
          readonly
        />
        <v-menu
          v-else
          ref="menuDateTo"
          v-model="menuDateTo"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
          :readonly="readonly"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="form.inhabitant.date_to | dateFormat"
              :label="`${$t('INHABITANTS.DATE_TO')} *`"
              :error-messages="errors.inhabitant && errors.inhabitant.date_to"
              :rules="getRulesByName('date_to')"
              append-icon="mdi-calendar"
              autocomplete="nope"
              role="button"
              clearable
              readonly
              @click:clear="clearTo"
              v-on="on"
            />
          </template>
          <v-date-picker
            ref="picker"
            v-model="form.inhabitant.date_to"
            :min="form.inhabitant.date_from"
            first-day-of-week="1"
            @change="saveDateto"
          />
        </v-menu>
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="form.inhabitant.info"
          value="Additional info"
          :label="$t('PROFILE.ADDITIONAL_INFO')"
          :rules="getRulesByName('info')"
          :error-messages="errors.inhabitant && errors.inhabitant.info"
          :readonly="readonly"
          :counter="1500"
          rows="4"
          @input="updateValue('inhabitant.info', $event)"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-divider />
    </v-row>
    <v-row align="center">
      <v-col md="auto">
        <v-checkbox
          v-model="form.notruf_subscription.cancel"
          :disabled="!notruf.id || futureNotruf"
          :label="$t('APARTMENTS.CANCEL_NOTRUF')"
          :readonly="readonly"
          :error-messages="
            errors.notruf_subscription && errors.notruf_subscription.cancel
          "
        />
      </v-col>
      <v-col v-if="form.notruf_subscription.cancel">
        <v-text-field
          v-if="readonly"
          :value="form.notruf_subscription.date_to | dateFormat"
          :label="`${$t('INHABITANTS.DATE_TO')} *`"
          append-icon="mdi-calendar"
          :error-messages="
            errors.notruf_subscription && errors.notruf_subscription.date_to
          "
          readonly
        />
        <v-menu
          v-else
          ref="menuDateToNotruf"
          v-model="menuDateToNotruf"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
          :readonly="readonly"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="form.notruf_subscription.date_to | dateFormat"
              :label="`${$t('INHABITANTS.DATE_TO')} *`"
              append-icon="mdi-calendar"
              clearable
              autocomplete="nope"
              readonly
              role="button"
              :error-messages="
                errors.notruf_subscription && errors.notruf_subscription.date_to
              "
              :rules="getRulesByName('notruf_subscription.date_to')"
              @click:clear="clearToNotruf"
              v-on="on"
            />
          </template>
          <v-date-picker
            ref="picker"
            v-model="form.notruf_subscription.date_to"
            :min="notrufMinDate"
            first-day-of-week="1"
            @change="saveDateToNotruf"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-divider />
    </v-row>
    <v-row align="center">
      <v-col md="auto" cols="auto" justify="end">
        <span class="text-body-2 mx-auto">{{ $t('PERSONS.SET_STATUS') }}:</span>
      </v-col>
      <v-col cols="auto">
        <v-select
          v-model="form.person.status_id"
          solo
          flat
          :items="statuses"
          :error-messages="errors.person && errors.person.status"
          item-value="id"
          item-text="name"
          small-chips
          hide-details
          dense
          :readonly="readonly"
          style="width: 130px"
        >
          <template v-slot:selection="{ attrs, item, select, selected }">
            <v-chip
              small
              :color="item.color"
              dark
              class="ml-1"
              v-bind="attrs"
              :input-value="selected"
              @click="select"
            >
              {{ item.name }}
            </v-chip>
          </template>
        </v-select>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';
import { mapCacheActions } from 'vuex-cache';
import { dateFormat, dateFormatBackend } from '@/modules/_global/filters';
import {
  notEmoji,
  notEmpty,
  maxLength
} from '@/modules/_global/variables/rules';
import formEditable from '@/modules/_global/mixins/form-editable';
import { maxDaysAgoCancelNotruf } from '@/modules/_global/config';

export default {
  name: 'CheckOut',

  filters: {
    dateFormat
  },

  mixins: [formEditable],

  data: () => ({
    valid: true,
    menuDateFrom: false,
    menuDateTo: false,
    menuDateToNotruf: false,
    statuses: [],
    form: {
      person: {},
      inhabitant: {},
      notruf_subscription: {
        cancel: false
      }
    },
    notruf: {
      id: null,
      to: null,
      from: null
    },
    rules: {
      date_from: [notEmpty],
      date_to: [notEmpty],
      info: [maxLength(1500), notEmoji],
      notruf_subscription: {
        date_to: [notEmpty]
      }
    }
  }),

  computed: {
    notrufBestBefore() {
      return dateFormatBackend(moment().subtract(maxDaysAgoCancelNotruf, 'd'));
    },
    notrufMinDate() {
      return moment(this.notrufBestBefore).isBefore(this.notruf.from)
        ? this.notruf.from
        : this.notrufBestBefore;
    },
    futureNotruf() {
      return moment().isBefore(this.notruf.from);
    }
  },

  mounted() {
    this.getStatuses().then(({ data }) => {
      this.statuses = data.data;
    });
    if (this.form.person.id) {
      this.getNotruf(this.form.person.id).then(({ data }) => {
        if (data) {
          this.form.notruf_subscription.date_to = dateFormatBackend(data.to);
          this.form.notruf_subscription.cancel = true;
        }
      });
    }
  },

  methods: {
    ...mapCacheActions('person', ['getStatuses']),
    ...mapActions('person', ['getPersonNotruf']),

    getNotruf(id) {
      return this.getPersonNotruf(id).then(({ data }) => {
        this.notruf = { ...this.notruf, ...data.data };
        return data;
      });
    },

    clearToNotruf() {
      this.form.notruf_subscription.date_to = undefined;
    },

    clearFrom() {
      this.form.inhabitant.date_from = undefined;
    },

    clearTo() {
      this.form.inhabitant.date_to = null;
    },

    saveDatefrom(date) {
      this.$refs.menuDateFrom.save(date);
      this.updateValue('inhabitant.date_from', date);
    },

    saveDateToNotruf(date) {
      this.$refs.menuDateToNotruf.save(date);
      this.updateValue('notruf_subscription.date_to', date);
    },

    saveDateto(date) {
      this.$refs.menuDateTo.save(date);
      this.updateValue('inhabitant.date_to', date);
    }
  }
};
</script>
